/**
 * 解析波函数：整片海 = 若干「方向性正弦波」的叠加。
 *
 * 为什么不用 FFT 海洋、也不用物理浮力：
 *  - 两端必须算出**逐帧一致**的海面，解析函数天然满足；
 *  - CPU 采样（船体姿态）与 GPU 顶点位移必须用**同一个公式**，否则船会「浮在浪外面」。
 * 所以这里只保留能被两侧精确重算的东西：一个数组 + 一个纯函数。
 *
 * 约定坐标系（与 three.js 一致）：Y 向上，船头朝 -Z。
 */

export type SeaState = 'calm' | 'swell' | 'rough';

export interface Wave {
  /** 传播方向（单位向量，XZ 平面） */
  readonly dirX: number;
  readonly dirZ: number;
  /** 振幅（米） */
  readonly amplitude: number;
  /** 波长（米） */
  readonly wavelength: number;
  /** 角波数 k = 2π/λ */
  readonly k: number;
  /** 角频率 ω = sqrt(g·k)（深水色散关系） */
  readonly omega: number;
  /** 初相（弧度） */
  readonly phase: number;
}

export const GRAVITY = 9.81;

/** 每个海况的基准振幅与最长波长。 */
const SEA_STATE_PRESET: Record<SeaState, { amplitude: number; wavelength: number; steepness: number }> = {
  calm: { amplitude: 0.14, wavelength: 46, steepness: 0.55 },
  swell: { amplitude: 0.42, wavelength: 42, steepness: 0.6 },
  rough: { amplitude: 0.8, wavelength: 38, steepness: 0.66 },
};

/**
 * 由 seed 生成一组波。
 * 相邻波的波长按 0.64 递减、振幅按 0.72 递减，方向在主导风向 ±55° 内散开。
 */
export function makeWaveSet(seed: number, seaState: SeaState = 'swell', count = 6): Wave[] {
  const preset = SEA_STATE_PRESET[seaState];
  // 用 splitmix32 保证「同 seed 同波组」
  const waves: Wave[] = [];
  let s = seed >>> 0;
  const next = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };

  const windAngle = next() * Math.PI * 2;
  const spread = 55 * (Math.PI / 180);

  for (let i = 0; i < count; i++) {
    const angle = windAngle + (next() * 2 - 1) * spread;
    const wavelength = preset.wavelength * Math.pow(0.64, i);
    const amplitude = preset.amplitude * Math.pow(0.72, i) * (0.85 + next() * 0.3);
    const k = (Math.PI * 2) / wavelength;
    waves.push({
      dirX: Math.cos(angle),
      dirZ: Math.sin(angle),
      amplitude,
      wavelength,
      k,
      omega: Math.sqrt(GRAVITY * k),
      phase: next() * Math.PI * 2,
    });
  }
  return waves;
}

/** 海面高度。CPU 与 shader 用同一公式，改这里就要改 Ocean 的 GLSL。 */
export function waveHeight(waves: readonly Wave[], x: number, z: number, time: number): number {
  let h = 0;
  for (let i = 0; i < waves.length; i++) {
    const w = waves[i]!;
    h += w.amplitude * Math.sin(w.k * (w.dirX * x + w.dirZ * z) - w.omega * time + w.phase);
  }
  return h;
}

export interface SurfaceSample {
  height: number;
  /** 单位法线（Y 向上） */
  nx: number;
  ny: number;
  nz: number;
}

/** 一次采样同时给出高度与法线（复用同一批中间量，省一次遍历）。 */
export function sampleSurface(
  waves: readonly Wave[],
  x: number,
  z: number,
  time: number,
  out: SurfaceSample = { height: 0, nx: 0, ny: 1, nz: 0 },
): SurfaceSample {
  let h = 0;
  let dhx = 0;
  let dhz = 0;
  for (let i = 0; i < waves.length; i++) {
    const w = waves[i]!;
    const phase = w.k * (w.dirX * x + w.dirZ * z) - w.omega * time + w.phase;
    const sin = Math.sin(phase);
    const cos = Math.cos(phase);
    h += w.amplitude * sin;
    const slope = w.amplitude * cos * w.k;
    dhx += slope * w.dirX;
    dhz += slope * w.dirZ;
  }
  // 法线 = normalize(-dh/dx, 1, -dh/dz)
  const len = Math.hypot(-dhx, 1, -dhz);
  out.height = h;
  out.nx = -dhx / len;
  out.ny = 1 / len;
  out.nz = -dhz / len;
  return out;
}

/** 理论最大波高，用于钳制相机/判定「是不是浪尖」。 */
export function maxAmplitude(waves: readonly Wave[]): number {
  let a = 0;
  for (let i = 0; i < waves.length; i++) a += waves[i]!.amplitude;
  return a;
}

export function seaStateLabel(state: SeaState): string {
  return state === 'calm' ? '平静' : state === 'swell' ? '微浪' : '大浪';
}
